
import { Component, Prop, Vue } from 'vue-property-decorator';
import Popup from '@/components/utils/Popup.vue';
import { DataService } from '@/service/DataService';
import { setFocusToInput, addErrorMessage, isGuest } from '@/utils/functions';
import { Room, RoomSpace, GuestPermission } from '@/utils/models';
import Alert from '../../utils/Alert.vue';
import { EventEmitter } from 'events';
import { Debouncer } from '../../../utils/debouncer';

@Component({ components: { Popup, Alert } })
export default class AddRoomPopup extends Vue {
  @Prop() private dataServiceRef!: DataService;
  @Prop() private selectedSpace!: RoomSpace | null;
  @Prop() isVisible!: boolean;
  @Prop() private email!: string;

  addingRoomErrorMessage = '';
  newRoomName = '';
  emailsToBeAllowed: string = '';
  guestPermission: GuestPermission = 'allow';
  isNewRoomReadyToAdd = false;
  alertEventEmitter = new EventEmitter();

  inputDebouncer = new Debouncer(3000);
  get rooms(): Room[] {
    return this.selectedSpace?.rooms ?? [];
  }
  init(): void {
    this.clearAddingRoomErrorMessage();
    this.clearAll();
    setFocusToInput(this.$refs.popupInputRef);
  }

  private clearAddingRoomErrorMessage(): void {
    this.addingRoomErrorMessage = '';
  }

  private clearAll(): void {
    this.newRoomName = '';
    this.guestPermission = 'allow';
    this.isNewRoomReadyToAdd = false;
    this.emailsToBeAllowed = '';
  }

  inputPermission(permission: GuestPermission) {
    this.guestPermission = permission;
  }

  onInput() {
    this.inputDebouncer.execute(this.parseNewRoomInput, 1000);
  }

  onNewRoomFormSubmit(event: Event): void {
    event.preventDefault();
    this.addRoom();
  }

  addRoom(): void {
    const allowUserEmails: string[] = [];
    if (this.emailsToBeAllowed.trim()) {
      allowUserEmails.push(...this.emailsToBeAllowed.split(','));
    }
    const onCreate = (error?: string) => {
      if (error) {
        this.addingRoomErrorMessage = error;
        this.alertEventEmitter.emit('shake');
      } else {
        this.hidePopup();
      }
    };
    this.parseNewRoomInput();
    if (this.isNewRoomReadyToAdd && this.selectedSpace) {
      this.dataServiceRef.emitAddRoom(
        this.selectedSpace.id,
        this.newRoomName,
        this.guestPermission,
        allowUserEmails,
        onCreate,
      );
    }
  }

  parseNewRoomInput(): void {
    if (!this.newRoomName?.trim()) {
      this.addingRoomErrorMessage = 'ルーム名は必須です。';
      this.alertEventEmitter.emit('shake');
      this.isNewRoomReadyToAdd = false;
      return;
    }
    if (this.emailsToBeAllowed.trim().length > 0) {
      const emails = this.emailsToBeAllowed.split(',');
      const emailRegex = /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/;
      for (let i = 0; i < emails.length; i++) {
        const email = emails[i];
        if (!emailRegex.test(email)) {
          this.addingRoomErrorMessage = email.trim()
            ? `メールアドレス ${email} の形式が不正です。`
            : `${i + 1}番目のメールアドレスを入力してください。`;
          this.isNewRoomReadyToAdd = false;
          this.alertEventEmitter.emit('shake');
          return;
        }
      }
    }

    if (this.selectedSpace) {
      this.clearAddingRoomErrorMessage();
      this.isNewRoomReadyToAdd = true;
    } else {
      this.addingRoomErrorMessage = 'スペースが存在しません。';
      this.isNewRoomReadyToAdd = false;
    }
  }

  hidePopup(): void {
    this.clearAddingRoomErrorMessage();
    this.clearAll();
    this.$emit('close');
  }
}
